<template>
  <div class="grid-x flex-grow settings-admin-form-mobile">
    <Loading class="flex-grow" :loading="saving">
      <div class="cell auto cell-block-y flex flex-col flex-grow">
        <div class="flex flex-col divide-y bg-hint">
          <div class="flex flex-row items-center justify-between py-6 px-8">
            <b class="text-18px leading-22px">Новый администратор</b>
          </div>

          <div v-if="!accessV2Ready" class="px-8 py-4 bg-warning">
            Backend Admin Access V2 ещё не активен. Создание администратора временно заблокировано,
            чтобы не сохранить неполный набор прав.
          </div>

          <div class="flex flex-col px-8 py-6 space-y-4">
            <input class="input" v-model="form.login" placeholder="Login">
            <input class="input" v-model="form.name" placeholder="Имя">
            <input class="input" v-model="form.phone" placeholder="Телефон">
            <input class="input" v-model="form.email" placeholder="Email">
            <input class="input" v-model="form.country" placeholder="Страна">
            <input class="input" v-model="form.region" placeholder="Регион">
            <input class="input" v-model="form.city" placeholder="Город">
          </div>

          <div class="flex flex-col px-8 py-6 space-y-4">
            <b class="text-16px leading-18px">Тип аккаунта</b>
            <select class="input" v-model="form.role" :disabled="!currentIsSuperAdmin">
              <option value="admin">Администратор</option>
              <option v-if="currentIsSuperAdmin" value="super_admin">Суперадминистратор</option>
            </select>
            <span v-if="form.role === 'super_admin'" class="text-13px text-muted">
              Суперадминистратор имеет полный доступ. Выбранные ниже права сохраняются как набор
              на случай последующего понижения роли.
            </span>
          </div>

          <div class="flex flex-col divide-y">
            <div class="px-8 py-4">
              <b class="text-16px leading-18px">Доступ и расширенные права</b>
            </div>
            <div
              v-for="item in visiblePermissions"
              :key="item.key"
              class="flex flex-row items-center space-x-8 px-8 py-5"
            >
              <div class="w-64">
                <b class="text-14px leading-16px">{{ item.label }}</b>
                <div class="text-12px text-muted mt-1">{{ item.description }}</div>
              </div>
              <Check
                label="Разрешено"
                :value="form.accessPermissions.includes(item.key)"
                :disabled="false"
                @input="(value) => togglePermission(item.key, value)"
              />
            </div>
          </div>

          <div class="px-8 py-5 bg-hint">
            <b class="text-14px leading-16px">Коммуникации в карточках</b>
            <div class="text-12px text-muted mt-1">
              Право «Коммуникации» управляет только общей очередью.
              Вкладки «Коммуникации» внутри карточек заказчика и исполнителя по-прежнему
              определяются правами «Заказчики» и «Исполнители».
            </div>
          </div>

          <div class="flex flex-row justify-end px-8 py-6">
            <button
              class="btn btn-primary"
              :disabled="saving || !accessV2Ready"
              @click="createAdminHandler"
            >
              Создать
            </button>
          </div>
        </div>
      </div>
    </Loading>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Check from '@/components/Check.vue';
import Loading from '@/components/Loading.vue';
import { createAdmin } from '@/api/settings';
import {
  ADMIN_PERMISSIONS,
  canAccess,
  hasAccessV2,
  isSuperAdmin,
  legacyPermissionsFromAccess,
} from '@/access/adminAccess';

const PERMISSION_ITEMS = [
  { key: ADMIN_PERMISSIONS.ORDERS, label: 'Заказы', description: 'Просмотр и работа с заказами.' },
  { key: ADMIN_PERMISSIONS.CONTRACTORS, label: 'Исполнители', description: 'Карточки исполнителей, операции и вкладка «Коммуникации» в карточке.' },
  { key: ADMIN_PERMISSIONS.COMMUNICATIONS, label: 'Коммуникации', description: 'Общая очередь «Коммуникации» и внешние Talk-Me диалоги.' },
  { key: ADMIN_PERMISSIONS.CUSTOMERS, label: 'Заказчики', description: 'Карточки заказчиков и вкладка «Коммуникации» в карточке.' },
  { key: ADMIN_PERMISSIONS.SUBJECTS, label: 'Объекты', description: 'Места захоронения и связанные операции.' },
  { key: ADMIN_PERMISSIONS.SERVICES, label: 'Услуги', description: 'Редактирование категорий, услуг и опций.' },
  { key: ADMIN_PERMISSIONS.PARTNERS, label: 'Реферралы', description: 'Управление партнёрами и партнёрскими страницами.' },
  { key: ADMIN_PERMISSIONS.SETTINGS, label: 'Настройки', description: 'Системные, юридические, SMS и региональные настройки.' },
  {
    key: ADMIN_PERMISSIONS.ADMINS,
    label: 'Администраторы',
    description: 'Создание и управление обычными администраторами.',
  },
  { key: ADMIN_PERMISSIONS.ANALYTICS, label: 'Аналитика', description: 'Business Analytics: продажи, клиенты, retention, регионы и операционные KPI.' },
  { key: ADMIN_PERMISSIONS.MONITORING, label: 'Мониторинг', description: 'System Health, инциденты и тестовые уведомления.' },
  { key: ADMIN_PERMISSIONS.DATABASE, label: 'База данных', description: 'Экспорт базы заказчиков и исполнителей.' },
];

export default {
  components: { Check, Loading },
  data() {
    return {
      saving: false,
      form: {
        login: '',
        name: '',
        phone: '',
        email: '',
        country: '',
        region: '',
        city: '',
        role: 'admin',
        accessPermissions: [],
      },
    };
  },
  computed: {
    ...mapState({ currentUser: (state) => state.auth.user }),
    accessV2Ready() { return hasAccessV2(this.currentUser); },
    currentIsSuperAdmin() { return isSuperAdmin(this.currentUser); },
    visiblePermissions() {
      if (this.currentIsSuperAdmin) return PERMISSION_ITEMS;
      return PERMISSION_ITEMS.filter((item) => canAccess(this.currentUser, item.key));
    },
  },
  methods: {
    togglePermission(permission, value) {
      const set = new Set(this.form.accessPermissions);
      if (value) set.add(permission); else set.delete(permission);
      this.form.accessPermissions = Array.from(set);
    },
    async createAdminHandler() {
      if (!this.accessV2Ready) return;
      if (!this.form.login || !this.form.name || !this.form.phone || !this.form.email
        || !this.form.country || !this.form.region || !this.form.city) {
        alert('Заполните все обязательные поля');
        return;
      }
      if (this.form.role === 'admin' && this.form.accessPermissions.length === 0) {
        alert('Назначьте администратору хотя бы одно право доступа');
        return;
      }

      try {
        this.saving = true;
        const accessPermissions = [...this.form.accessPermissions];
        await createAdmin({
          login: this.form.login,
          name: this.form.name,
          phone: this.form.phone,
          email: this.form.email,
          country: this.form.country,
          region: this.form.region,
          city: this.form.city,
          role: this.form.role,
          accessPermissions,
          permissions: legacyPermissionsFromAccess(accessPermissions),
        });
        alert('Администратор создан');
        this.$router.push({ name: 'settings.admins' });
      } catch (e) {
        console.error(e);
        alert('Ошибка создания администратора');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
